// src/pages/sales/VisitLogModal.jsx
import { useState } from "react";
import { ModalWrap } from "../../components/ModalWrap";
import { todayDubai } from "../../utils/dateUtils";

const CHANNELS = ["PHONE", "VISIT", "EMAIL", "WHATSAPP"];

const CHANNEL_LABEL = {
  PHONE:    "Phone",
  VISIT:    "Visit",
  EMAIL:    "Email",
  WHATSAPP: "WhatsApp",
};

function CustomerSearch({ accounts, value, onChange }) {
  const selected = accounts.find(a => String(a.id) === String(value));
  const [query, setQuery] = useState(selected?.name ?? "");
  const [open,  setOpen]  = useState(false);

  const matches = query.trim().length === 0
    ? accounts
    : accounts.filter(a => a.name.toLowerCase().includes(query.toLowerCase()));

  function pick(a) { onChange(String(a.id)); setQuery(a.name); setOpen(false); }
  function handleInput(e) { setQuery(e.target.value); onChange(""); setOpen(true); }
  function handleBlur() { setTimeout(() => { setOpen(false); if (!value) setQuery(""); }, 150); }

  return (
    <div style={{ position:"relative" }}>
      <input className="form-input" style={{ width:"100%" }}
        value={query} onChange={handleInput}
        onFocus={() => setOpen(true)} onBlur={handleBlur}
        placeholder="Search customer…" autoComplete="off" />
      {open && matches.length > 0 && (
        <div style={{ position:"absolute", top:"100%", left:0, right:0, zIndex:20,
          background:"#fff", border:"1px solid #d1d5db", borderRadius:6,
          boxShadow:"0 4px 12px rgba(0,0,0,0.12)", maxHeight:200, overflowY:"auto", marginTop:2 }}>
          {matches.map(a => (
            <div key={a.id} onMouseDown={() => pick(a)}
              style={{ padding:"7px 12px", fontSize:13, cursor:"pointer", display:"flex", alignItems:"center", gap:6,
                background: String(a.id) === String(value) ? "#eff6ff" : "transparent" }}
              onMouseEnter={e => e.currentTarget.style.background = "#f3f4f6"}
              onMouseLeave={e => e.currentTarget.style.background = String(a.id) === String(value) ? "#eff6ff" : "transparent"}>
              {a.name}
              {a.status === "INACTIVE" && (
                <span style={{ fontSize:9, fontWeight:600, color:"#9ca3af", background:"#f3f4f6", border:"1px solid #e5e7eb", borderRadius:3, padding:"1px 4px" }}>INACTIVE</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function VisitLogModal({ accounts, onSave, onCancel }) {
  const [isProspect,    setIsProspect]    = useState(false);
  const [accountId,     setAccountId]     = useState("");
  const [prospectName,  setProspectName]  = useState("");
  const [contactDate,   setContactDate]   = useState(todayDubai());
  const [channel,       setChannel]       = useState("PHONE");
  const [summary,       setSummary]       = useState("");
  const [salesAed,      setSalesAed]      = useState("");
  const [collectionAed, setCollectionAed] = useState("");
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState(null);

  const customerValid = isProspect
    ? prospectName.trim().length > 0
    : accountId !== "";

  const valid = customerValid && contactDate && channel;

  async function handleSave() {
    if (!valid) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({
        accountId:     isProspect ? null : Number(accountId),
        prospectName:  isProspect ? prospectName.trim() : null,
        contactDate,
        channel,
        summary:       summary.trim() || null,
        salesAed:      salesAed      ? parseFloat(salesAed)      : null,
        collectionAed: collectionAed ? parseFloat(collectionAed) : null,
      });
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  }

  return (
    <ModalWrap title="Log Customer Contact" onClose={onCancel}>
      {/* Customer */}
      <div style={{ marginBottom:12 }}>
        <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:8 }}>
          <span className="form-label" style={{ minWidth:110 }}>Customer</span>
          <label style={{ fontSize:12, display:"flex", gap:4, alignItems:"center", cursor:"pointer" }}>
            <input type="checkbox" checked={isProspect}
              onChange={e => { setIsProspect(e.target.checked); setAccountId(""); setProspectName(""); }} />
            New prospect (not in CRM)
          </label>
        </div>
        {isProspect ? (
          <input className="form-input" style={{ width:"100%" }} placeholder="Prospect name…"
            value={prospectName} onChange={e => setProspectName(e.target.value)} autoFocus />
        ) : (
          <CustomerSearch accounts={accounts} value={accountId} onChange={setAccountId} />
        )}
      </div>

      {/* Contact date */}
      <div className="form-row">
        <span className="form-label">Contact Date</span>
        <input className="form-input" style={{ flex:1 }} type="date"
          value={contactDate} onChange={e => setContactDate(e.target.value)} />
      </div>

      {/* Channel */}
      <div className="form-row">
        <span className="form-label">Channel</span>
        <div style={{ display:"flex", gap:6, flex:1 }}>
          {CHANNELS.map(ch => (
            <button key={ch}
              className={channel === ch ? "btn-primary" : "btn"}
              style={{ flex:1, padding:"5px 0", fontSize:12 }}
              onClick={() => setChannel(ch)}>
              {CHANNEL_LABEL[ch]}
            </button>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="form-row" style={{ alignItems:"flex-start" }}>
        <span className="form-label" style={{ paddingTop:4 }}>Summary</span>
        <textarea className="form-input" style={{ flex:1, minHeight:72, resize:"vertical" }}
          placeholder="Brief notes on what was discussed…"
          value={summary} onChange={e => setSummary(e.target.value)} />
      </div>

      {/* AED fields */}
      <div className="form-row">
        <span className="form-label">Sales (AED)</span>
        <input className="form-input input-right" style={{ width:140 }} type="number" step="0.01" min="0"
          placeholder="optional" value={salesAed} onChange={e => setSalesAed(e.target.value)} />
      </div>
      <div className="form-row">
        <span className="form-label">Collection (AED)</span>
        <input className="form-input input-right" style={{ width:140 }} type="number" step="0.01" min="0"
          placeholder="optional" value={collectionAed} onChange={e => setCollectionAed(e.target.value)} />
      </div>

      {error && <div className="alert-warn" style={{ marginBottom:8 }}>{error}</div>}

      <div className="form-sep" />
      <div className="form-footer">
        <button className="btn" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="btn-primary" disabled={!valid || saving} onClick={handleSave}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </ModalWrap>
  );
}
